import { useState, useEffect } from 'react'
import styled, { css } from 'styled-components/macro'
import intersection from 'lodash/intersection'
import { LocalizedText } from 'localization'

function matchType(a, b) {
  return !!intersection(a, b).length
}

const hover = css`
  background: ${(props) => props.theme.colors.primaryDark};
  color: ${(props) => props.theme.colors.white};
`

const Address = styled.div`
  background: ${(props) => props.theme.colors.background};
  padding: 1rem;
  color: ${(props) => props.theme.colors.black};
  cursor: pointer;
  border: 1px solid ${(props) => props.theme.colors.greyLighter};
  border-top: none;

  &:hover {
    ${hover}
  }

  ${(props) => props.selected && hover}
`

const types = {
  address: {
    search: ['geocode', 'establishment'],
    filter: ['street_address', 'premise', 'point_of_interest'],
  },
  gig_estimate: {
    search: ['geocode', 'establishment'],
  },
}

async function fetchSuggestions(text, type = 'address') {
  const { filter, search } = types[type]
  const service = new window.google.maps.places.AutocompleteService()
  const matches =
    (await new Promise((res) => {
      service.getPlacePredictions(
        {
          input: text,
          types: search,
          componentRestrictions: { country: 'us' },
        },
        res
      )
    })) || []
  if (!filter) {
    return matches
  }
  return matches.filter((p) => matchType(p.types, filter))
}

async function fetchPlace(id) {
  const service = new window.google.maps.places.PlacesService(document.createElement('div'))
  const details = await new Promise((res) => {
    service.getDetails({ placeId: id }, res)
  })
  return details
}

export function addressText(address) {
  if (address.text) {
    return address.text
  }

  const { street, city, state, zip } = address

  const stateAndZip = [state, zip].filter((x) => x).join(' ')
  const addressStr = [street, city, stateAndZip].filter((x) => x).join(', ')

  return addressStr
}

export function getPlaceBreakdown(place) {
  const captured = {}
  place.address_components.forEach((ac) => {
    ac.types.forEach((type) => {
      captured[type] = captured[type] || ac
    })
  })

  const latitude = place.geometry.location.lat()
  const longitude = place.geometry.location.lng()

  const grab = (...fields) => {
    const found = fields.find((f) => captured[f])
    return found && captured[found]
  }

  const number = grab('street_number')
  const street = grab('street_address')
  const route = grab('route')
  const city = grab('locality', 'sublocality', 'neighborhood', 'administrative_area_level_3')
  const state = grab('administrative_area_level_1')
  const zip = grab('postal_code')

  const friendly_name = place.name && place.name.startsWith(number && number.long_name) ? undefined : place.name

  const streetValue = street
    ? street.long_name
    : route && number
      ? `${number.long_name} ${route.short_name}`
      : undefined
  const cityValue = city && city.long_name
  const stateValue = state && state.short_name
  const zipValue = zip && zip.long_name

  const stateAndZip = [stateValue, zipValue].filter((x) => x).join(' ')
  const addressStr = [streetValue, cityValue, stateAndZip].filter((x) => x).join(', ')

  return {
    street: streetValue,
    city: cityValue,
    state: stateValue,
    zip: zipValue,
    latitude,
    longitude,
    friendly_name,
    text: addressStr,
  }
}

function processMatch(match) {
  const parts = []
  let desc = match.description
  let cursor = 0
  let k = Math.random()
  match.matched_substrings.forEach((ms) => {
    if (ms.offset !== cursor) {
      const text = desc.substring(cursor, ms.offset)
      parts.push(<span key={k++}>{text}</span>)
    }
    cursor = ms.offset + ms.length
    const text = desc.substring(ms.offset, cursor)
    parts.push(<strong key={k++}>{text}</strong>)
  })
  if (cursor !== desc.length) {
    const text = desc.substring(cursor, desc.length)
    parts.push(<span key={k++}>{text}</span>)
  }
  return parts
}

const AddressList = (props) => {
  const [matches, setMatches] = useState()
  const { input, addressType, resultSelected, enterPressed, onSelect, onManual } = props

  useEffect(() => {
    if (input) {
      fetchSuggestions(input, addressType).then(setMatches)
    }
  }, [input, addressType])

  const handleSelect = (id) => {
    fetchPlace(id).then((place) => {
      onSelect(getPlaceBreakdown(place))
    })
  }

  if (enterPressed) {
    handleSelect(matches[resultSelected % matches.length].place_id)
    // return null
  }

  if (!input) {
    return null
  }

  if (!matches) {
    return null
  }

  let results = []

  if (matches.length) {
    results = matches.map((m, i) => (
      <Address key={m.id} selected={i === resultSelected % matches.length} onMouseDown={() => handleSelect(m.place_id)}>
        {processMatch(m)}
      </Address>
    ))
  }

  if (onManual) {
    results.push(
      <Address key="manual-entry" selected={matches.length === resultSelected % matches.length} onMouseDown={onManual}>
        <LocalizedText translationKey="dweb_form_address_list_cant_find_address_click_here" />
      </Address>
    )
  }

  return results
}

export default AddressList
